import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import { formatDate } from "@/lib/format";
import { BackLink, PageContainer, PageHeader } from "@/components/PagePrimitives";
import { HeaderRow, LoadingState, TH, TableShell, prettyCategory } from "@/components/ExpenseUi";
import { ExpensesTable } from "@/components/ExpensesTable";
import { formatSpend } from "@/components/MerchantsPage";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import type { components } from "@dolfin/api-admin";

type MerchantDetailResponse = components["schemas"]["MerchantDetailResponse"];

export function MerchantDetailPage({
  merchantId,
  onBack,
  onOpenExpense,
}: {
  merchantId: string;
  onBack?: () => void;
  onOpenExpense?: (expenseId: string) => void;
}) {
  const api = useDolfinClient();
  const [merchant, setMerchant] = useState<MerchantDetailResponse | null>(null);

  useEffect(() => {
    api.GET("/v1/merchants/{id}", { params: { path: { id: merchantId } } }).then(({ data, error }) => {
      if (error || !data) toast.error(apiErrorMessage(error, "Couldn't load the merchant."));
      setMerchant(data ?? null);
    });
  }, [api, merchantId]);

  if (!merchant) {
    return (
      <PageContainer>
        {onBack ? <BackLink onClick={onBack}>Back to merchants</BackLink> : null}
        <LoadingState label="Loading merchant..." />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {onBack ? <BackLink onClick={onBack}>Back to merchants</BackLink> : null}
      <PageHeader
        eyebrow="Merchant"
        title={merchant.displayName ?? "Merchant"}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-base font-semibold text-foreground tabular-nums">{formatSpend(merchant.spend)}</span>
            <span>
              {merchant.expenseCount} expense{merchant.expenseCount === 1 ? "" : "s"}
            </span>
            <span aria-hidden>·</span>
            <span>{prettyCategory(merchant.categoryLabel) ?? "Uncategorised"}</span>
            <span aria-hidden>·</span>
            <span>First seen {formatDate(merchant.firstSeenAt)}</span>
          </span>
        }
      />

      <h2 className="mb-2 text-sm font-semibold">How the card networks spell it</h2>
      <TableShell>
        <Table>
          <TableHeader>
            <HeaderRow>
              <TableHead className={TH}>On the statement</TableHead>
              <TableHead className={TH}>Cleaned</TableHead>
              <TableHead className={TH}>How it was linked</TableHead>
              <TableHead className={TH}>First seen</TableHead>
            </HeaderRow>
          </TableHeader>
          <TableBody>
            {(merchant.aliases ?? []).map((a) => (
              <TableRow key={a.descriptor}>
                <TableCell className="font-mono text-xs">
                  {a.seenAs?.length ? a.seenAs.join(", ") : <span className="text-muted-foreground">—</span>}
                </TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">{a.descriptor}</TableCell>
                <TableCell>
                  {a.method === "Similar" ? (
                    <Badge
                      variant="outline"
                      className="border-primary/30 text-primary"
                      title={a.distance != null ? `Distance ${Number(a.distance).toFixed(3)}` : undefined}
                    >
                      Recognised automatically
                    </Badge>
                  ) : (
                    <span className="text-sm text-muted-foreground">First spelling</span>
                  )}
                </TableCell>
                <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                  {formatDate(a.firstSeenAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableShell>

      <h2 className="mt-8 mb-2 text-sm font-semibold">Spend here</h2>
      <ExpensesTable merchantId={merchantId} onSelect={onOpenExpense} />
    </PageContainer>
  );
}
